import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, RotateCcw, Send, Pencil, Trash2, StickyNote, MessageSquare, ArrowUpRight, CheckCheck, Archive, Bot } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { useGuard } from "@/lib/guards";
import { UserIdentifiersEditor } from "@/components/admin/UserIdentifiersEditor";
import { KeyRound } from "lucide-react";
import { TicketPhotoPicker, TicketPhotoGallery, readyPhotoUrls } from "@/components/support/TicketPhotos";
import { useTicketThread } from "@/components/support/useTicketThread";
import { UnreadDivider, NewBelowPill, UnreadBadge } from "@/components/support/ThreadBits";
import { EscalationChip, EscalationControls, StaleChip } from "@/components/admin/TicketEscalation";
import { HandlerChip, ViewersChip, OwnershipBar, TicketHistory, useTicketPresence } from "@/components/admin/TicketOwnership";
import { QuickRepliesBar, QuickReplyProposalsCard } from "@/components/admin/TicketQuickReplies";
import { StaleAlertChip, StaleAlertsCard } from "@/components/admin/TicketStaleAlerts";
import { AutoCloseCard, FeedbackChip } from "@/components/admin/TicketAutoClose";
import { SupportTeamsCard } from "@/components/admin/SupportTeamsCard";
import { LeadBoardCard } from "@/components/admin/LeadBoardCard";
import { ConfirmCard, PromptCard } from "@/components/admin/YalolaCards";
import { TicketMessageText } from "@/components/support/TicketTranslation";
import { usePager, PagerBar } from "@/components/admin/usePager";

const STATUS_LABELS = { open: "Ouvert", resolved: "Résolu", closed: "Clôturé", escalated: "Transférés", mine: "À moi", unassigned: "Sans responsable" };
const NOTE_KIND_LABEL = { note: "Note interne", claim: "Prise en charge", takeover: "Reprise", release: "Libération", escalation: "Transfert", escalation_cancel: "Reprise" };

const MatchChip = ({ ok, label }) => (
  <span className="chip text-[10px]" style={ok ? { background: "#D1FAE5", color: "#065F46" } : { background: "#FEE2E2", color: "#991B1B" }}>{label} : {ok ? "correspond" : "ne correspond pas"}</span>
);

// « Récupération de compte » ticket: what the member claimed, whether it matches the
// account, and (super admin) one click to apply the new number / e-mail.
// Lot 604 — demandes libre-service : statut automatique (délai 24 h / validation manuelle) + Appliquer maintenant / Annuler.
const AUTO_LABELS = { pending: "Application automatique programmée", manual: "Validation manuelle requise (compte protégé)", applied: "Appliquée", cancelled: "Annulée" };
const SENSITIVE_FR = { role: "rôle d'équipe", wallet: "solde du portefeuille", church: "page d'église" };
const fmtFr = (iso) => { try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "long", timeStyle: "short" }); } catch { return iso || ""; } };

const RecoveryAutoBlock = ({ ticket, isSuper, onChanged }) => {
  const a = ticket.recovery?.auto;
  const [busy, setBusy] = useState(false);
  const [asking, setAsking] = useState(null);   // lot 774 — carte Yalola à la place du dialogue du navigateur
  if (!a) return null;
  const status = ticket.recovery?.applied ? "applied" : a.status;
  const act = async (action, reason) => {
    setAsking(null);
    setBusy(true);
    try {
      await api.post(`/admin/recoveries/${a.recovery_id}/${action}`, { reason });
      toast.success(action === "apply" ? "Changement appliqué : le membre peut se connecter avec son nouvel identifiant." : "Demande annulée.");
      onChanged?.();
    } catch (e) { toast.error(e?.friendlyMessage || e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  const tone = { pending: { background: "#FEF3C7", color: "#92400E" }, manual: { background: "#FEE2E2", color: "#991B1B" }, applied: { background: "#D1FAE5", color: "#065F46" }, cancelled: { background: "#E5E7EB", color: "#374151" } }[status] || {};
  return (
    <div className="rounded-xl p-3 space-y-2 text-xs" style={{ background: "var(--yalola-surface-alt)" }} data-testid="ticket-recovery-auto" data-status={status}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="chip text-[10px]" style={tone} data-testid="ticket-recovery-auto-status">{AUTO_LABELS[status] || status}</span>
        <span className="chip text-[10px]" style={{ background: "#D1FAE5", color: "#065F46" }}>Nouveau canal vérifié par code ✓</span>
        {status === "pending" && a.apply_at && <span data-testid="ticket-recovery-auto-when">Sauf opposition du membre, appliquée le <b>{fmtFr(a.apply_at)}</b> (délai {a.delay_hours || 24} h).</span>}
        {a.reminded_at && <span className="chip text-[10px]" style={{ background: "#EFF6FF", color: "#1D4ED8" }} data-testid="ticket-recovery-auto-reminded">⏰ Rappel ~1 h envoyé le {fmtFr(a.reminded_at)}</span>}
        {status === "manual" && <span data-testid="ticket-recovery-auto-reasons">Compte protégé : {(a.sensitive_reasons || []).map((r) => SENSITIVE_FR[r] || r).join(", ") || "—"}. Vérifie l'identité puis applique.</span>}
      </div>
      {(status === "pending" || status === "manual") && (
        <div className="flex flex-wrap gap-2">
          {isSuper && <Button size="sm" className="rounded-full pill-primary h-8" onClick={() => setAsking("apply")} disabled={busy} data-testid="ticket-recovery-apply-now">Appliquer maintenant</Button>}
          <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => setAsking("cancel")} disabled={busy} data-testid="ticket-recovery-cancel">Annuler la demande</Button>
        </div>
      )}
      <PromptCard open={!!asking} onOpenChange={(v) => !v && setAsking(null)} busy={busy} onConfirm={(reason) => act(asking, reason)} testId="ticket-recovery-reason-card"
        title={asking === "cancel" ? "Annuler la demande de récupération" : "Appliquer le changement d'identifiant"}
        description={asking === "cancel" ? "Motif de l'annulation (journalisé, visible dans le ticket)." : "Motif (journalisé)."}
        defaultValue={asking === "cancel" ? "" : "Identité vérifiée par le support"} placeholder="Motif…" confirmLabel={asking === "cancel" ? "Annuler la demande" : "Appliquer maintenant"} />
    </div>
  );
};

const RecoveryBlock = ({ ticket, isSuper, onApplied }) => {
  const r = ticket.recovery || {};
  const m = r.matches || {};
  return (
    <div className="yalola-card p-4 space-y-3" style={{ border: "1px solid var(--yalola-primary)" }} data-testid="ticket-recovery-block">
      <div className="font-semibold flex items-center gap-2"><KeyRound size={16} style={{ color: "var(--yalola-primary)" }} /> Récupération de compte {r.applied && <span className="chip text-[10px]" style={{ background: "#D1FAE5", color: "#065F46" }} data-testid="ticket-recovery-applied">Appliquée</span>}</div>
      <RecoveryAutoBlock ticket={ticket} isSuper={isSuper} onChanged={onApplied} />
      <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
        <div><b>Nom déclaré :</b> {r.claimed_name}</div>
        <div><b>Ancien identifiant :</b> {r.old_identifier}</div>
        <div><b>Nouveau numéro :</b> {r.new_phone || "—"}</div>
        <div><b>Nouvel e-mail :</b> {r.new_email || "—"}</div>
        <div><b>E-mail de contact :</b> {r.contact_email || "—"}</div>
        <div><b>Naissance / ville déclarées :</b> {r.birth_date || "—"} · {r.city || "—"}</div>
      </div>
      {r.user_found ? (
        <div className="flex gap-1.5 flex-wrap" data-testid="ticket-recovery-matches">
          <span className="chip text-[10px]" style={{ background: "#D1FAE5", color: "#065F46" }}>Compte trouvé : {ticket.user?.name || "—"}</span>
          <MatchChip ok={!!m.name} label="Nom" />
          {r.birth_date && <MatchChip ok={!!m.birth_date} label="Date de naissance" />}
          {r.city && <MatchChip ok={!!m.city} label="Ville" />}
        </div>
      ) : (
        <div className="text-xs" style={{ color: "#991B1B" }} data-testid="ticket-recovery-notfound">Aucun compte ne correspond à cet identifiant. Réponds au demandeur (e-mail de contact) pour obtenir le bon identifiant.</div>
      )}
      {r.user_found && ticket.user && !r.applied && (
        <UserIdentifiersEditor user={ticket.user} isSuper={isSuper} presetPhone={r.new_phone || ""} presetEmail={r.new_email || ""} ticketId={ticket.id} onSaved={onApplied} />
      )}
    </div>
  );
};

export const TicketsPanel = ({ canManage = true }) => {
  const { user: me, lang, config } = useApp();
  const isSuper = (me?.roles || [me?.role]).includes("super_admin");
  // Module de traduction (même réglage que le Fil) : les messages du membre dans une autre langue sont traduits d'office.
  const viewerLang = (me?.language || lang || "fr").toLowerCase().slice(0, 2);
  const translationOn = config?.settings?.features?.post_translation !== false;
  const [status, setStatus] = useState("open");
  const [tickets, setTickets] = useState([]);
  const ticketPager = usePager(tickets);
  const [counts, setCounts] = useState({ open: 0, resolved: 0 });
  const [selected, setSelected] = useState(null);
  // lot 780 — repli + interrupteur par fonctionnalité des lots 776 → 780
  const chatOn = useGuard("support_thread_chat");
  const alertsOn = useGuard("support_thread_alerts");
  const receiptsOn = useGuard("support_read_receipts");
  const autocloseOn = useGuard("support_autoclose");
  const feedbackOn = useGuard("support_feedback");
  // lot 776 — fil comme un chat : ouverture sur les non lus, descente à l'envoi, nouveaux messages sans quitter le ticket
  const thread = useTicketThread({ ticketId: selected?.id, meId: me?.id, onTicket: setSelected, chat: chatOn, alerts: alertsOn, titleLabel: (n) => (n > 1 ? "Nouveaux messages" : "Nouveau message") });
  const { messages } = thread;
  // lot 777 — « Vu » sous la dernière réponse de l'équipe lue par le membre (dernière lecture du fil côté /support)
  const lastTeamReply = [...messages].reverse().find((m) => m.is_admin && !m.internal);
  const seenAt = receiptsOn && selected?.member_read_at && lastTeamReply && selected.member_read_at >= lastTeamReply.created_at ? selected.member_read_at : null;
  const [reply, setReply] = useState("");
  const [replyPhotos, setReplyPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState(null);
  const [staleHours, setStaleHours] = useState(48);   // lot 770 — seuil « sans réponse » réglé par le super admin
  // lot 769 — prise en charge / présence / notes internes / édition
  const ownershipOn = useGuard("support_ownership");
  const teamLeadsOn = useGuard("support_team_leads");   // lot 774
  const [noteMode, setNoteMode] = useState(false);
  const [editing, setEditing] = useState(null); // { id, content }
  const viewers = useTicketPresence(selected?.id, ownershipOn && !!selected);
  const handler = selected?.handler;
  const lockedForMe = ownershipOn && selected?.status === "open" && !!handler && handler.id !== me?.id;

  useEffect(() => {
    api.get("/admin/tickets/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    api.get(`/admin/tickets${status !== "all" ? `?status=${status}` : ""}`)
      .then(({ data }) => {
        setTickets(data.tickets || []);
        setCounts({ open: data.open_count || 0, resolved: data.resolved_count || 0, escalated: data.escalated_count || 0, mine: data.mine_count || 0, unassigned: data.unassigned_count || 0 });
        if (data.stale_hours) setStaleHours(data.stale_hours);
      })
      .catch(() => {});
  }, [status]);
  useEffect(() => { load(); }, [load]);
  // lot 769 — la liste se rafraîchit toutes les 30 s (présence « X est dessus », prises en charge des collègues)
  useEffect(() => {
    if (!ownershipOn || selected) return undefined;
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [ownershipOn, selected, load]);

  const openTicket = async (t, { refresh = false } = {}) => {
    try {
      const { data } = await api.get(`/support/tickets/${t.id}`);
      setSelected(data.ticket);
      if (refresh) thread.sync(data); else { thread.open(data); setReplyPhotos([]); }
    } catch { toast.error("Erreur"); }
  };
  const refreshTicket = () => openTicket(selected, { refresh: true });

  // Lien direct depuis une notification : /admin?tab=tickets&ticket=<id> ouvre le ticket. Le paramètre reste dans
  // l'URL (un remontage du panneau au chargement à froid rouvre le même ticket) ; il est retiré au « Retour ».
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinked = useRef(null);
  useEffect(() => {
    const tid = searchParams.get("ticket");
    if (!tid || deepLinked.current === tid) return;
    deepLinked.current = tid;
    openTicket({ id: tid });
  }, [searchParams]);
  const closeTicket = () => {
    setSelected(null);
    load();
    if (searchParams.get("ticket")) { const next = new URLSearchParams(searchParams); next.delete("ticket"); setSearchParams(next, { replace: true }); }
  };

  const send = async () => {
    const attachments = noteMode ? [] : readyPhotoUrls(replyPhotos);
    if ((!reply.trim() && !attachments.length) || !selected) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/support/tickets/${selected.id}/messages`, { content: reply.trim(), attachments, internal: noteMode });
      thread.append(data.message);
      setReply("");
      setReplyPhotos([]);
      if (!noteMode && !handler) refreshTicket(); // première réponse → le ticket m'est attribué
      load();
    } catch (e) {
      toast.error(e?.friendlyMessage || e?.response?.data?.detail || "Erreur");
      if (e?.response?.status === 409) refreshTicket();   // repris par un collègue entre-temps → rafraîchir
    }
    finally { setBusy(false); }
  };

  // lot 769 — modifier une réponse / note de l'équipe (auteur ou super admin) ; supprimer un message (super admin)
  const saveEdit = async () => {
    if (!editing || !selected) return;
    setBusy(true);
    try {
      const { data } = await api.put(`/support/tickets/${selected.id}/messages/${editing.id}`, { content: editing.content.trim() });
      thread.setMessages((ms) => ms.map((m) => (m.id === editing.id ? { ...m, ...data.message } : m)));
      setEditing(null);
      toast.success("Message modifié");
    } catch (e) { toast.error(e?.friendlyMessage || e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  const [confirmDel, setConfirmDel] = useState(null);   // lot 774 — { kind: "message", m } | { kind: "ticket" }
  const deleteMessage = async (m) => {
    if (!selected) return;
    setConfirmDel(null);
    try {
      await api.delete(`/support/tickets/${selected.id}/messages/${m.id}`);
      thread.setMessages((ms) => ms.filter((x) => x.id !== m.id));
      toast.success("Message supprimé");
    } catch (e) { toast.error(e?.friendlyMessage || e?.response?.data?.detail || "Erreur"); }
  };
  const deleteTicket = async () => {
    if (!selected) return;
    setConfirmDel(null);
    try {
      await api.delete(`/admin/tickets/${selected.id}`);
      toast.success("Ticket supprimé");
      closeTicket();
    } catch (e) { toast.error(e?.friendlyMessage || e?.response?.data?.detail || "Erreur"); }
  };
  const canEdit = (m) => canManage && m.is_admin && (m.sender_id === me?.id || isSuper);

  const act = async (action) => {
    if (!selected) return;
    try {
      await api.post(`/admin/tickets/${selected.id}/action`, { action });
      toast.success(action === "resolve" ? "Ticket résolu" : "Ticket réouvert");
      refreshTicket();
      load();
    } catch (e) {
      toast.error(e?.friendlyMessage || e?.response?.data?.detail || "Erreur");
      if (e?.response?.status === 409) { refreshTicket(); load(); }   // déjà traité par un collègue → rafraîchir
    }
  };

  if (selected) {
    return (
      <div className="space-y-3" data-testid="admin-ticket-detail">
        <div className="yalola-card p-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <Button size="sm" variant="outline" className="rounded-full" onClick={closeTicket} data-testid="ticket-back-btn">
              <ArrowLeft size={14} className="mr-1" /> Retour
            </Button>
            <div className="min-w-0">
              <div className="font-semibold truncate">{selected.subject}</div>
              <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>
                {selected.user?.name || selected.recovery?.claimed_name || "Compte non identifié"} · {selected.user?.email || selected.user?.phone || selected.recovery?.contact_email || "—"} · <span className="chip text-[10px]">{STATUS_LABELS[selected.status] || selected.status}</span>
              </div>
              {(selected.status === "resolved" || selected.status === "closed") && selected.resolved_at && (
                <div className="text-xs mt-0.5 font-medium inline-flex items-center gap-1" style={{ color: "#15803D" }} data-testid="ticket-resolved-by">
                  <CheckCircle2 size={12} /> Résolu par {selected.resolved_by_user?.name || selected.resolved_by_name || "l'équipe"} le {new Date(selected.resolved_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                </div>
              )}
              {selected.status === "closed" && selected.closed_at && (
                <div className="text-xs mt-0.5 inline-flex items-center gap-1" style={{ color: "#0F766E" }} data-testid="ticket-autoclosed-at">
                  <Archive size={12} /> {selected.closed_by === "auto" ? "Clôturé automatiquement" : "Clôturé"} le {new Date(selected.closed_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })} — sans réaction du membre
                </div>
              )}
              <div className="mt-1 flex flex-wrap gap-1"><EscalationChip ticket={selected} testId="ticket-escalation-chip" /><StaleChip ticket={selected} threshold={staleHours} testId="ticket-stale-chip" /><StaleAlertChip ticket={selected} />{feedbackOn && <FeedbackChip ticket={selected} />}</div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <EscalationControls ticket={selected} canManage={canManage} onChanged={() => { refreshTicket(); load(); }} />
            {selected.status === "open" ? (
              <Button size="sm" className="pill-primary rounded-full" onClick={() => act("resolve")} disabled={!canManage} data-testid="ticket-resolve-btn">
                <CheckCircle2 size={14} className="mr-1" /> Marquer résolu
              </Button>
            ) : (
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => act("reopen")} disabled={!canManage} data-testid="ticket-reopen-btn">
                <RotateCcw size={14} className="mr-1" /> Réouvrir
              </Button>
            )}
            {isSuper && (
              <Button size="sm" variant="outline" className="rounded-full" style={{ color: "#B91C1C" }} onClick={() => setConfirmDel({ kind: "ticket" })} data-testid="ticket-delete-btn">
                <Trash2 size={14} className="mr-1" /> Supprimer
              </Button>
            )}
          </div>
        </div>
        {ownershipOn && <OwnershipBar ticket={selected} me={me?.id} isSuper={isSuper} canManage={canManage} viewers={viewers} onChanged={() => { refreshTicket(); load(); }} />}
        <ConfirmCard open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)} danger confirmLabel="Supprimer définitivement" testId="ticket-delete-card"
          title={confirmDel?.kind === "ticket" ? `Supprimer le ticket « ${selected.subject} » ?` : "Supprimer ce message du fil ?"}
          description={confirmDel?.kind === "ticket" ? "Tout le fil (réponses, notes internes, photos) est supprimé. Cette action est irréversible." : "Le message disparaît définitivement du fil pour l'équipe et pour le membre."}
          onConfirm={() => (confirmDel?.kind === "ticket" ? deleteTicket() : deleteMessage(confirmDel.m))} />
        {selected.recovery && <RecoveryBlock ticket={selected} isSuper={isSuper} onApplied={refreshTicket} />}
        <div className="relative">
        <div ref={thread.boxRef} onScroll={thread.onScroll} className="yalola-card p-4 space-y-3 max-h-[50vh] overflow-y-auto" data-testid="ticket-messages">
          {messages.map((m) => {
            const divider = thread.firstUnreadId === m.id && <UnreadDivider key={`unread-${m.id}`} testId="ticket-unread-divider" />;
            const tools = (canEdit(m) || isSuper) && (
              <span className="inline-flex gap-1 ml-2 align-middle" data-testid={`ticket-msg-tools-${m.id}`}>
                {canEdit(m) && <button type="button" className="opacity-60 hover:opacity-100" title="Modifier" onClick={() => setEditing({ id: m.id, content: m.content || "" })} data-testid={`ticket-msg-edit-${m.id}`}><Pencil size={11} /></button>}
                {isSuper && <button type="button" className="opacity-60 hover:opacity-100" title="Supprimer (super admin)" onClick={() => setConfirmDel({ kind: "message", m })} data-testid={`ticket-msg-delete-${m.id}`}><Trash2 size={11} /></button>}
              </span>
            );
            const edited = m.edited_at && <span className="text-[10px] opacity-70 ml-1" title={`Modifié par ${m.edited_by_name || "équipe"} le ${new Date(m.edited_at).toLocaleString()}`} data-testid={`ticket-msg-edited-${m.id}`}>(modifié)</span>;
            const editor = editing?.id === m.id && (
              <div className="mt-2 space-y-1.5" data-testid={`ticket-msg-editor-${m.id}`}>
                <Textarea rows={3} value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })} data-testid={`ticket-msg-edit-input-${m.id}`} />
                <div className="flex gap-1.5 justify-end">
                  <Button size="sm" variant="outline" className="rounded-full h-7" onClick={() => setEditing(null)} data-testid={`ticket-msg-edit-cancel-${m.id}`}>Annuler</Button>
                  <Button size="sm" className="pill-primary rounded-full h-7" disabled={busy || (!editing.content.trim() && !(m.attachments || []).length)} onClick={saveEdit} data-testid={`ticket-msg-edit-save-${m.id}`}>Enregistrer</Button>
                </div>
              </div>
            );
            return m.internal ? (
              <React.Fragment key={m.id}>{divider}
              <div data-mid={m.id} className="ml-auto max-w-[85%] rounded-xl p-3 text-sm border border-dashed" style={{ borderColor: "#C4B5FD", background: "#F5F3FF", color: "#4C1D95" }} data-testid={`ticket-internal-note-${m.id}`} data-kind={m.kind || "escalation"}>
                <div className="text-[11px] font-semibold mb-0.5 inline-flex items-center gap-1 flex-wrap">{m.kind === "note" ? <StickyNote size={11} /> : <ArrowUpRight size={11} />} {NOTE_KIND_LABEL[m.kind] || "Note interne"} · {m.sender?.name || "équipe"} — visible par l'équipe seulement{edited}{tools}</div>
                {editor || <div className="whitespace-pre-wrap">{m.content}</div>}
                <div className="text-[10px] mt-1 opacity-70">{new Date(m.created_at).toLocaleString()}</div>
              </div>
              </React.Fragment>
            ) : (
            <React.Fragment key={m.id}>{divider}
            <div data-mid={m.id} className={`max-w-[85%] ${m.is_admin ? "ml-auto" : ""}`}>
              <div className="rounded-xl p-3 text-sm" style={{ background: m.is_admin ? "var(--yalola-primary-soft)" : "var(--yalola-surface-alt)" }}>
                <div className="text-[11px] font-semibold mb-0.5">{m.system ? <span className="inline-flex items-center gap-1" data-testid={`ticket-msg-system-${m.id}`}><Bot size={11} /> Message automatique</span> : <>{m.sender?.name || "—"}{m.is_admin ? " (équipe)" : ""}</>}{edited}{!m.system && tools}</div>
                {editor || <TicketMessageText message={m} ticketId={selected.id} viewerLang={viewerLang} auto={!m.is_admin} enabled={translationOn} testId={`ticket-msg-text-${m.id}`} />}
                <TicketPhotoGallery photos={m.attachments} testId={`ticket-msg-photos-${m.id}`} />
                <div className="text-[10px] mt-1" style={{ color: "var(--yalola-text-muted)" }}>{new Date(m.created_at).toLocaleString()}</div>
              </div>
              {seenAt && lastTeamReply?.id === m.id && (
                <div className="text-[10px] mt-0.5 text-right inline-flex items-center gap-1 w-full justify-end" style={{ color: "#15803D" }} title={`Lu par le membre le ${new Date(seenAt).toLocaleString()}`} data-testid="ticket-msg-seen">
                  <CheckCheck size={11} /> Vu · {new Date(seenAt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                </div>
              )}
            </div>
            </React.Fragment>
            );
          })}
        </div>
        <NewBelowPill count={thread.pendingBelow} onClick={() => thread.scrollToBottom(true)} testId="ticket-new-below" />
        </div>
        <TicketHistory ticket={selected} />
        <div className="yalola-card p-4 space-y-2">
          <QuickRepliesBar onPick={setReply} canManage={canManage} isSuper={isSuper} />
          <div className="flex gap-1.5 flex-wrap items-center" data-testid="ticket-compose-mode">
            <button type="button" onClick={() => setNoteMode(false)} className={`pill text-xs inline-flex items-center gap-1 ${!noteMode ? "pill-primary" : "pill-outline"}`} aria-pressed={!noteMode} data-testid="ticket-mode-reply"><MessageSquare size={12} /> Répondre au membre</button>
            <button type="button" onClick={() => setNoteMode(true)} className={`pill text-xs inline-flex items-center gap-1 ${noteMode ? "pill-primary" : "pill-outline"}`} aria-pressed={noteMode} data-testid="ticket-mode-note"><StickyNote size={12} /> Note interne</button>
            {noteMode && <span className="text-[11px]" style={{ color: "#6D28D9" }} data-testid="ticket-note-hint">Visible par l'équipe seulement — le membre ne la voit jamais.</span>}
            {!noteMode && lockedForMe && <span className="text-[11px]" style={{ color: "#92400E" }} data-testid="ticket-locked-compose">Pris en charge par {handler.name} — reprends le ticket pour répondre.</span>}
          </div>
          <div className="flex gap-2 items-end">
            <Textarea rows={2} placeholder={noteMode ? "Note interne pour l'équipe…" : lockedForMe ? `Pris en charge par ${handler.name} — reprends le ticket pour répondre` : "Répondre au membre..."} value={reply} onChange={(e) => setReply(e.target.value)} disabled={!canManage || (lockedForMe && !noteMode)} style={noteMode ? { borderColor: "#C4B5FD", background: "#F5F3FF" } : undefined} data-testid="ticket-reply-input" />
            <Button className="pill-primary rounded-full" onClick={send} disabled={busy || !canManage || (lockedForMe && !noteMode) || replyPhotos.some((p) => p.uploading) || (!reply.trim() && !readyPhotoUrls(replyPhotos).length)} data-testid="ticket-reply-send">
              {noteMode ? <StickyNote size={15} /> : <Send size={15} />}
            </Button>
          </div>
          {canManage && !noteMode && <TicketPhotoPicker photos={replyPhotos} onChange={setReplyPhotos} disabled={busy || lockedForMe} testId="ticket-reply-photos" />}
        </div>
      </div>
    );
  }

  const maxWeek = stats ? Math.max(1, ...stats.weeks.map((w) => Math.max(w.created, w.resolved))) : 1;

  return (
    <div className="space-y-3" data-testid="admin-tickets-panel">
      {stats && (
        <div className={`grid gap-3 grid-cols-2 ${feedbackOn && stats.satisfaction ? "lg:grid-cols-5" : "lg:grid-cols-4"}`} data-testid="ticket-stats-cards">
          <div className="yalola-card p-4">
            <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>1re réponse (30 j)</div>
            <div className="text-xl font-bold font-display mt-1" data-testid="ticket-stat-response">
              {stats.avg_first_response_minutes === null ? "—" : stats.avg_first_response_minutes < 60 ? `${Math.round(stats.avg_first_response_minutes)} min` : `${(stats.avg_first_response_minutes / 60).toFixed(1)} h`}
            </div>
          </div>
          <div className="yalola-card p-4">
            <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>Résolution (30 j)</div>
            <div className="text-xl font-bold font-display mt-1" data-testid="ticket-stat-resolution">
              {stats.avg_resolution_hours === null ? "—" : stats.avg_resolution_hours < 1 ? `${Math.round(stats.avg_resolution_hours * 60)} min` : `${stats.avg_resolution_hours.toFixed(1)} h`}
            </div>
          </div>
          <div className="yalola-card p-4">
            <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>Ouverts</div>
            <div className="text-xl font-bold font-display mt-1" style={{ color: "var(--yalola-primary)" }}>{stats.open_count}</div>
          </div>
          <div className="yalola-card p-4">
            <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>Résolus</div>
            <div className="text-xl font-bold font-display mt-1" style={{ color: "var(--yalola-secondary)" }}>{stats.resolved_count}</div>
          </div>
          {feedbackOn && stats.satisfaction && (
            <div className="yalola-card p-4" data-testid="ticket-stat-satisfaction" data-up={stats.satisfaction.up} data-down={stats.satisfaction.down}>
              <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>Satisfaction (30 j)</div>
              <div className="text-xl font-bold font-display mt-1" style={{ color: "#166534" }}>
                {stats.satisfaction.up + stats.satisfaction.down === 0 ? "—" : `${Math.round((stats.satisfaction.up / (stats.satisfaction.up + stats.satisfaction.down)) * 100)} % 👍`}
              </div>
              <div className="text-[10px]" style={{ color: "var(--yalola-text-muted)" }}>{stats.satisfaction.up + stats.satisfaction.down} avis · {stats.satisfaction.up} 👍 / {stats.satisfaction.down} 👎</div>
            </div>
          )}
        </div>
      )}
      {stats && (
        <div className="yalola-card p-4" data-testid="ticket-stats-chart">
          <div className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: "var(--yalola-text-muted)" }}>Volume par semaine (8 dernières)</div>
          <div className="flex items-end gap-2 h-24">
            {stats.weeks.map((w) => (
              <div key={w.week} className="flex-1 flex flex-col items-center gap-1" title={`${w.week} : ${w.created} créé(s), ${w.resolved} résolu(s)`}>
                <div className="w-full flex items-end justify-center gap-0.5 flex-1">
                  <div className="w-2.5 rounded-t" style={{ height: `${(w.created / maxWeek) * 100}%`, minHeight: w.created ? 4 : 1, background: "var(--yalola-primary)" }} />
                  <div className="w-2.5 rounded-t" style={{ height: `${(w.resolved / maxWeek) * 100}%`, minHeight: w.resolved ? 4 : 1, background: "var(--yalola-secondary)" }} />
                </div>
                <div className="text-[9px]" style={{ color: "var(--yalola-text-muted)" }}>{w.week}</div>
              </div>
            ))}
          </div>
          <div className="flex gap-4 mt-2 text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full inline-block" style={{ background: "var(--yalola-primary)" }} /> Créés</span>
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full inline-block" style={{ background: "var(--yalola-secondary)" }} /> Résolus</span>
          </div>
        </div>
      )}
      {/* Réglages (super admin seulement) : un agent ne voit ni les cartes ni leurs explications */}
      {isSuper && <StaleAlertsCard isSuper onRan={load} />}
      {isSuper && autocloseOn && <AutoCloseCard isSuper onRan={load} />}
      <QuickReplyProposalsCard isSuper={isSuper} />
      {ownershipOn && teamLeadsOn && <LeadBoardCard onOpenTicket={(id) => openTicket({ id })} onChanged={load} />}
      {ownershipOn && teamLeadsOn && <SupportTeamsCard me={me?.id} />}
      <div className="yalola-card p-4 flex gap-2 flex-wrap">
        {["open", ...(ownershipOn ? ["unassigned"] : []), "escalated", "mine", "resolved", "all"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} className={`pill ${status === s ? "pill-primary" : "pill-outline"}`} data-testid={`tickets-filter-${s}`}>
            {s === "all" ? "Tous" : STATUS_LABELS[s]}{s === "open" ? ` (${counts.open})` : s === "resolved" ? ` (${counts.resolved})` : s === "escalated" ? ` (${counts.escalated || 0})` : s === "mine" ? ` (${counts.mine || 0})` : s === "unassigned" ? ` (${counts.unassigned || 0})` : ""}
          </button>
        ))}
      </div>
      {tickets.length === 0 && <div className="yalola-card p-8 text-center" style={{ color: "var(--yalola-text-muted)" }} data-testid="tickets-empty">{status === "escalated" ? "Aucun ticket transféré." : status === "mine" ? "Aucun ticket ne t'est assigné." : status === "unassigned" ? "Tous les tickets ouverts ont un responsable." : "Aucun ticket."}</div>}
      <div className="space-y-2">
        {ticketPager.slice.map((t) => (
          <button key={t.id} onClick={() => openTicket(t)} className="w-full text-left yalola-card p-4 hover:shadow-md transition-shadow" data-testid={`ticket-row-${t.id}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="font-semibold truncate flex items-center gap-2 flex-wrap">{t.subject}<UnreadBadge count={chatOn ? t.unread_count : 0} testId={`ticket-row-unread-${t.id}`} /><EscalationChip ticket={t} compact testId={`ticket-row-escalated-${t.id}`} /><StaleChip ticket={t} threshold={staleHours} testId={`ticket-row-stale-${t.id}`} /><StaleAlertChip ticket={t} testId={`ticket-row-stale-alert-${t.id}`} />{feedbackOn && <FeedbackChip ticket={t} compact testId={`ticket-row-feedback-${t.id}`} />}{ownershipOn && <HandlerChip ticket={t} me={me?.id} compact testId={`ticket-row-handler-${t.id}`} />}{ownershipOn && <ViewersChip viewers={t.viewers} testId={`ticket-row-viewers-${t.id}`} />}</div>
                <div className="text-xs truncate" style={{ color: "var(--yalola-text-muted)" }}>{t.recovery ? "🔑 " : ""}{t.user?.name || t.recovery?.claimed_name || "—"} · {t.last_message}</div>
              </div>
              <div className="text-right shrink-0">
                <span className="chip text-[10px]" style={t.status === "open" ? { background: "#FEF3C7", color: "#92400E" } : t.status === "closed" ? { background: "#CCFBF1", color: "#115E59" } : {}}>{STATUS_LABELS[t.status] || t.status}</span>
                <div className="text-[10px] mt-1" style={{ color: "var(--yalola-text-muted)" }}>{new Date(t.updated_at).toLocaleDateString()}</div>
                {(t.status === "resolved" || t.status === "closed") && (t.resolved_by_user?.name || t.resolved_by_name) && (
                  <div className="text-[10px] font-medium" style={{ color: "#15803D" }} data-testid={`ticket-row-resolved-by-${t.id}`}>par {t.resolved_by_user?.name || t.resolved_by_name}</div>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>
      <PagerBar page={ticketPager.page} pages={ticketPager.pages} total={ticketPager.total} shown={ticketPager.slice.length} onPage={ticketPager.setPage} noun="ticket" testId="tickets-pager" />
    </div>
  );
};
